import React from 'react';
import { useTranslation } from 'react-i18next';
import { Package, ShoppingCart, MessageSquare, DollarSign } from 'lucide-react';
import { useSellerStats } from '../hooks/queries/sellerQueries';

const Stats: React.FC = () => {
  const { t } = useTranslation(['stats', 'dashboard']);
  const { data: stats, isLoading } = useSellerStats();

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">{t('subtitle')}</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">{t('stats.totalProducts', { ns: 'dashboard' })}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{stats?.products.total || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{stats?.products.active || 0} {t('stats.activeProducts', { ns: 'dashboard' })}</p>
            </div>
            <div className="bg-blue-500 p-2 md:p-3 rounded-lg flex-shrink-0 ml-2">
              <Package size={20} className="text-white md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">{t('stats.totalOrders', { ns: 'dashboard' })}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{stats?.orders.total || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{stats?.orders.completed || 0} {t('stats.completedOrders', { ns: 'dashboard' })}</p>
            </div>
            <div className="bg-green-500 p-2 md:p-3 rounded-lg flex-shrink-0 ml-2">
              <ShoppingCart size={20} className="text-white md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">{t('stats.totalBids', { ns: 'dashboard' })}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{stats?.bids.total || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{stats?.bids.accepted || 0} {t('stats.acceptedBids', { ns: 'dashboard' })}</p>
            </div>
            <div className="bg-purple-500 p-2 md:p-3 rounded-lg flex-shrink-0 ml-2">
              <MessageSquare size={20} className="text-white md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">{t('stats.totalEarnings', { ns: 'dashboard' })}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">
                ${stats?.earnings.total.toLocaleString() || '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('stats.totalEarnings', { ns: 'dashboard' })}</p>
            </div>
            <div className="bg-yellow-500 p-2 md:p-3 rounded-lg flex-shrink-0 ml-2">
              <DollarSign size={20} className="text-white md:w-6 md:h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">{t('overview')}</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.totalOrders', { ns: 'dashboard' })}</span>
              <span className="font-semibold">{stats?.orders.total || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.pendingOrders', { ns: 'dashboard' })}</span>
              <span className="font-semibold text-yellow-600">{stats?.orders.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.completedOrders', { ns: 'dashboard' })}</span>
              <span className="font-semibold text-green-600">{stats?.orders.completed || 0}</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('overview')}</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.totalBids', { ns: 'dashboard' })}</span>
              <span className="font-semibold">{stats?.bids.total || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.pendingBids', { ns: 'dashboard' })}</span>
              <span className="font-semibold text-yellow-600">{stats?.bids.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('stats.acceptedBids', { ns: 'dashboard' })}</span>
              <span className="font-semibold text-green-600">{stats?.bids.accepted || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats;

